import { createAsyncThunk } from '@reduxjs/toolkit';
import oidcAuthApi from '~/domain/auth/api/oidc-auth-api';
import { clearInitialData, readInitialCurrentUser } from '~/app/initial-data';
import type { CurrentUserInfo } from './data/AuthenticationState';
import { getDiamGroupId } from '~/domain/uptime/utils/utils';

/**
 * Thunk to fetch the current user information.
 * It first checks for pre-fetched data in the global initial data, fetched from index.html.
 * If not available, it dispatches an API call to fetch the current user.
 * This thunk is needed so we use new RTK Query api but still leverage current state management,
 * and now enhancing with initial data.
 */
export const fetchCurrentUserThunk = createAsyncThunk<CurrentUserInfo, void>(
  'authentication/fetchCurrentUser',
  async (_, thunkAPI) => {
    const groupId = getDiamGroupId();
    if (groupId === undefined) {
      const initialUser = await readInitialCurrentUser();
      // The prefetched user is a launch-time snapshot (possibly served from the service worker
      // cache) — consume it once so later fetches go to the API.
      clearInitialData();
      if (initialUser) {
        return initialUser;
      }
    }

    const queryResult = thunkAPI.dispatch(oidcAuthApi.endpoints.fetchCurrentUser.initiate());

    try {
      const result = await queryResult;
      if ('data' in result && result.data) {
        return result.data;
      }
      const maybeError = (result as { error?: unknown }).error;
      throw maybeError instanceof Error ? maybeError : new Error('Failed to fetch current user');
    } finally {
      queryResult.unsubscribe();
    }
  }
);
